import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Local files
import { Root, Header, Title, ListContainer, ExpandButton, LocationRow, LocationName, LocationMessage, CloseButton, StyledCloseIcon } from './LocationsInspectionBanner.styled';
import useCustomSelector from 'hooks/useCustomSelector';
import useLocations from 'hooks/useLocations';
import useError from 'hooks/useError';
import useRole from 'hooks/useRole';
import { getInspectionAttentionStatus } from 'helpers/storageFacilityInspectionRenewal';

const CHIP_BY_VARIANT = {
  missing: { label: 'No inspection', color: 'error' },
  expired: { label: 'Expired', color: 'error' },
  draft: { label: 'Awaiting completion', color: 'warning' },
  renewal_due: { label: 'Expiring soon', color: 'warning' }
};

const LocationsInspectionBanner = () => {
  const { isManager, isOwner } = useRole();
  const { getLocations, clearLocalLocations } = useLocations();
  const { setError } = useError();
  const { data } = useCustomSelector(state => state.locations.all);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const enabled = isManager || isOwner;
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;
    const fetchAll = (offset) =>
      getLocations({ offset })
        .then(({ payload: { data: { pagination } } }) => {
          if (cancelled) return;
          if (pagination.offset + pagination.count < pagination.total_count) {
            return fetchAll(pagination.offset + pagination.limit);
          }
          setLoaded(true);
        })
        .catch((error) => !cancelled && setError(error));

    fetchAll(0);

    return () => {
      cancelled = true;
      clearLocalLocations();
    };
  }, [enabled, getLocations, clearLocalLocations, setError]);

  const attentionLocations = useMemo(() => {
    if (!loaded) return [];

    // Location is shared across incident/destination/customer addresses too — only
    // locations that belong to a company are actual storage yards worth flagging here.
    return data
      .filter((location) => !!location.company?.id)
      .map((location) => ({ location, attention: getInspectionAttentionStatus(location.storage_facility_inspection_reports) }))
      .filter(({ attention }) => attention.show);
  }, [data, loaded]);

  if (!enabled || dismissed || !attentionLocations.length) return null;

  return (
    <Root>
      <Header onClick={() => setExpanded(prev => !prev)}>
        <WarningAmberIcon color="warning" fontSize="small" />
        <Title>Storage inspections need attention ({attentionLocations.length})</Title>
        <ExpandButton
          aria-label={expanded ? 'collapse' : 'expand'}
          size="small"
          sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </ExpandButton>
        <CloseButton aria-label="close" size="small" onClick={(e) => { e.stopPropagation(); setDismissed(true); }}>
          <StyledCloseIcon />
        </CloseButton>
      </Header>
      <Collapse in={expanded}>
        <ListContainer>
          {attentionLocations.map(({ location, attention }) => {
            const chip = CHIP_BY_VARIANT[attention.variant];

            return (
              <LocationRow key={location.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationName>{location.name}</LocationName>
                  {!!chip && <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />}
                </Box>
                <LocationMessage>{attention.message}</LocationMessage>
              </LocationRow>
            );
          })}
        </ListContainer>
      </Collapse>
    </Root>
  );
};

export default memo(LocationsInspectionBanner);
